import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import './TermsModal.css';

const TermsModal = ({ isOpen, onClose }) => {
  const [openSection, setOpenSection] = useState(0); // First section open by default

  const sections = [
    {
      title: "Acceptance of Terms",
      content: (
        <div>
          <p>By accessing and using the GuiltBox platform, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our platform.</p>
          <p>You must be at least 16 years old to use this platform. By using GuiltBox, you represent and warrant that you meet this age requirement.</p>
        </div>
      )
    },
    {
      title: "Platform Description",
      content: (
        <div>
          <p>GuiltBox is a platform that allows users to share personal confessions which are transformed into crypto tokens on the Zora platform. These tokens generate donations for selected charitable organizations through trading activities.</p>
          <p>The platform operates on a decentralized model where user confessions become tradeable digital assets, with proceeds supporting charitable causes.</p>
        </div>
      )
    },
    {
      title: "Content Guidelines and Restrictions",
      content: (
        <div>
          <p><strong>Permitted Content:</strong> You may share personal stories, confessions, mistakes, or experiences from your own life. Content should be authentic and truthful to your personal experience.</p>
          <p><strong>Prohibited Content:</strong> The following types of content are strictly prohibited:</p>
          <ul>
            <li>Incitement to hatred, violence, or discrimination</li>
            <li>Content promoting pedophilia or child exploitation</li>
            <li>Content that actively encourages illegal activities</li>
            <li>Spam or promotional material unrelated to personal confession</li>
          </ul>
          <p><strong>Important Note:</strong> If you are sharing experiences involving prohibited activities that you regret, you may do so provided the content is not promotional or encouraging of such behavior toward others.</p>
        </div>
      )
    },
    {
      title: "Content Ownership and Rights",
      content: (
        <div>
          <p>By submitting content to GuiltBox, you grant the platform full ownership rights to your shared confessions. This includes the right to tokenize, distribute, and monetize your content through our platform mechanisms.</p>
          <p>Once submitted, confessions cannot be retracted, deleted, or refunded. Please consider this carefully before sharing your story.</p>
          <p>You retain moral rights to be identified as the creator of your confession, but all commercial and distribution rights transfer to GuiltBox upon submission.</p>
        </div>
      )
    },
    {
      title: "Financial Terms and Donations",
      content: (
        <div>
          <p><strong>No User Fees:</strong> Creating and submitting confessions is completely free for users. There are no platform fees, creation fees, or trading fees charged to confession creators.</p>
          <p><strong>Revenue Distribution:</strong> When tokens are traded on our platform, revenue is distributed as follows:</p>
          <ul>
            <li>25% to each of the 3 selected charitable organizations (75% total)</li>
            <li>25% to the GuiltBox platform for operations and maintenance</li>
          </ul>
          <p><strong>Donation Processing:</strong> Donations are collected in platform-managed accounts and transferred monthly to charitable organizations through Crypto for Charity services.</p>
        </div>
      )
    },
    {
      title: "Privacy and Data Protection",
      content: (
        <div>
          <p>GuiltBox does not collect or process personal identifying information from users. The platform operates without user accounts or registration requirements.</p>
          <p>If personal information about identifiable individuals is mentioned in confessions, users may report such content through our reporting system for review and potential removal.</p>
          <p>All confessions are public by design and will be accessible to the general public through our platform and associated blockchain networks.</p>
        </div>
      )
    },
    {
      title: "Platform Responsibilities and Limitations",
      content: (
        <div>
          <p><strong>Content Responsibility:</strong> GuiltBox accepts no responsibility for the content of user confessions. Users are solely responsible for ensuring their content complies with applicable laws and regulations.</p>
          <p><strong>Moderation:</strong> The platform does not pre-moderate content. Users can report inappropriate content through our reporting system, which we will investigate on a case-by-case basis.</p>
          <p><strong>Platform Availability:</strong> While we strive to maintain consistent service, we cannot guarantee uninterrupted platform availability and accept no liability for service interruptions.</p>
        </div>
      )
    },
    {
      title: "Reporting and Enforcement",
      content: (
        <div>
          <p>Users can report problematic content through the reporting feature available on each confession. Reports are reviewed by our team, and appropriate action will be taken based on these terms.</p>
          <p>Violations of these terms may result in content removal. In cases of severe violations, we reserve the right to restrict platform access as deemed necessary.</p>
          <p>All enforcement decisions are made at the sole discretion of GuiltBox.</p>
        </div>
      )
    },
    {
      title: "Disclaimers and Limitation of Liability",
      content: (
        <div>
          <p>The GuiltBox platform is provided "as is" without warranties of any kind. We disclaim all warranties, express or implied, including but not limited to merchantability, fitness for a particular purpose, and non-infringement.</p>
          <p>GuiltBox shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the platform.</p>
          <p>Users acknowledge that cryptocurrency and blockchain technologies involve inherent risks, and GuiltBox accepts no responsibility for market volatility or technical issues with underlying blockchain networks.</p>
        </div>
      )
    },
    {
      title: "Changes to Terms",
      content: (
        <div>
          <p>GuiltBox reserves the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to the platform.</p>
          <p>Continued use of the platform after changes constitutes acceptance of the modified terms. Users are encouraged to review these terms periodically.</p>
          <p>For questions about these terms, please contact us through our platform's contact mechanisms.</p>
        </div>
      )
    }
  ];

  const toggleSection = (index) => {
    setOpenSection(openSection === index ? -1 : index);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="terms-modal-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="terms-modal"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="terms-modal-header">
            <h2>Terms of Service</h2>
            <button 
              className="terms-modal-close"
              onClick={onClose}
              aria-label="Close Terms"
            >
              <X size={24} />
            </button>
          </div>

          <div className="terms-modal-content">
            <div className="terms-intro">
              <p><strong>Last Updated:</strong> June 2025</p>
              <p>Welcome to GuiltBox. Please read these Terms of Service carefully before using our platform.</p>
            </div>

            {sections.map((section, index) => (
              <div key={index} className="terms-section">
                <button
                  className={`terms-section-header ${openSection === index ? 'active' : ''}`}
                  onClick={() => toggleSection(index)}
                >
                  <span>{index + 1}. {section.title}</span>
                  {openSection === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
                
                <AnimatePresence>
                  {openSection === index && (
                    <motion.div
                      className="terms-section-content"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                      <div className="terms-section-text">
                        {section.content}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}

            <div className="terms-footer">
              <p>By using GuiltBox, you acknowledge that you have read and understood these Terms of Service.</p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TermsModal;